<template>
  <div class="page__registration" :class="{'edit-mode': isEditMode}" @click="hideLayer">
    <div v-if="!trackingCodeGuideData">
      <div class="page__registration__step--wrap">
        <p class="title">{{ title }}</p>
        <div v-if="!isEditMode" class="step--wrap">
          <ol class="step__list essential">
            <li v-for="(step, index) in 2"
                class="list-item"
                :key="step"
                :class="{on: step === currentStep, available: isStepAvailable(step), step2__available: (index === 1 && isStepAvailable(step))}"
                @click="onStepClick(step)">
              <div :class="{ pass1: step === 1 && isStep1Valid && currentStep > 1, pass2: step === 2 && isStep2Valid && currentStep > 2 }" class="icon--wrap">
                <span class="step-num">{{ step }}</span>
                <i class="icon icon--check-white-size20"></i>
                <p class="step-title"><strong>{{ $t(stepData[step].titleKey) }}</strong></p>
                <span class="step-level">
                  <template v-if="stepData[step].isOptional">({{ $t('common.options') }})</template>
                  <template v-else>({{ $t('common.essential') }})</template>
                </span>
              </div>
            </li>
          </ol>
          <ul class="step__list optional">
            <li v-for="step in [3,4]"
                class="list-item"
                :key="step"
                :class="{on: step === currentStep, available: isStepAvailable(step), free: currentStep === 3 || currentStep === 4}"
                @click="onStepClick(step)">
              <div class="icon--wrap">
                <span class="icon--circle"></span>
                <p class="step-title"><strong>{{ $t(stepData[step].titleKey) }}</strong></p>
                <span class="step-level">
                    <template v-if="stepData[step].isOptional">({{ $t('common.options') }})</template>
                    <template v-else>({{ $t('common.essential') }})</template>
                  </span>
              </div>
            </li>
          </ul>
        </div>
        <div v-else class="edit-step--wrap">
          <ol class="edit-step__list clearfix">
            <li v-for="step in 4"
                class="list-item"
                :key="step"
                :class="{on: step === currentStep, disabled: step === 4 && editApplyState === 'run'}"
                @click="onStepClick(step)">
              <p class="step-title"><strong>{{ $t(stepData[step].titleKey) }}</strong></p>
            </li>
          </ol>
        </div>
      </div>
      <form class="page__registration__content--wrap"
        :class="{ page_edit: isEditMode }"
        @submit.prevent>
        <!-- left content -->
        <div class="page__registration__content left" :class="{ page_edit: isEditMode, step4: currentStep === 4 }">
          <!-- step 1 : Name -->
          <div v-if="currentStep === 1"
               class="step__box step1"
               :class="{ has_error: !isStep1Valid }">
            <p class="step-title">
              <span class="title__text">{{ $t(stepData[currentStep].titleKey) }}</span>
            </p>
            <registration-step1
              :isEditMode="isEditMode"
              :matchTypeOptions="matchTypeOptions"
              :matchTypeData="matchTypeData"
              :matchType.sync="model.matchType"
              :url.sync="model.url"
              :captureUrl.sync="model.captureUrl"
              :matchString.sync="model.matchString"
              :bLogin.sync="model.bLogin"
              :bMobile.sync="model.bMobile"
              :showMatchTypeGuide.sync="showMatchTypeGuide"
              :urlSiteCode="urlSiteCode"
              :useCaptureUrl.sync="useCaptureUrl"
              :requireDomain="requireDomain"
              :urlError="urlError"
              :matchStringError="matchStringError"
              :captureUrlError="captureUrlError"
              @validateUrl="validateUrlFirst"/>
          </div>

          <!-- step 2 : Page Targeting -->
          <div v-if="currentStep === 2"
               class="step__box step2"
               :class="{ has_error: !isStep2Valid }">
            <p class="step-title">
              <span class="title__text">{{ $t(stepData[currentStep].titleKey) }}</span>
            </p>
            <registration-step2
              :groupName="model.groupName"
              :groupNameDirect="groupNameDirect"
              :pageName="model.pageName"
              :url="model.url"
              :urlSiteCode="urlSiteCode"
              @onGroupNameChange="model.groupName = $event"
              @onGroupNameDirectChange="groupNameDirect = $event"
              @onPageNameChange="model.pageName = $event"/>
          </div>

          <!-- step 3 : option -->
          <div v-if="currentStep === 3"
               class="step__box step3 optional"
               :class="{ on: this.currentStep === 3}"
               @click="currentStep = 3">
            <p class="step-title">
              <span class="title__text">{{ $t(stepData[currentStep].titleKey) }}</span>
            </p>
            <registration-step3
              :isEditMode="isEditMode"
              :pvLimit.sync="model.pvLimit"
              :startDate.sync="model.startDate"
              :endDate.sync="model.endDate"
              :editApplyRegDate="editApplyRegDate"
              :editApplyMeta="model.meta"
              :editApplyState="editApplyState"
              :exceptionSelector.sync="model.exceptionSelector"
              :timezoneName="model.timezoneName"
              :timezone="model.timezone"
              @onTimezoneChange="onTimezoneChange"/>
          </div>

          <!-- step4 : preview -->
          <div v-if="currentStep === 4"
               class="step__box step4 optional"
               :class="{ on: this.currentStep === 4}"
               @click="currentStep = 4">
            <registration-step4
              :layer="'default'"
              :stepData="stepData"
              :currentStep="currentStep"
              :captureState="captureState"
              :captureResult="captureResult"
              :bMobile="model.bMobile"
              :canCapturePreview="canCapturePreview"
              :selectedDevice.sync="selectedDevice"
              @openShowRenderingLayer="startPreviewCapture"
              @openShowPreviewLayer="showPreviewLayer = true"/>
          </div>
        </div>
        <!-- divide -->
        <div class="divide"></div>
        <!-- right content -->
        <div class="page__registration__content right">
          <registration-guide :currentStep="currentStep"/>
        </div>
      </form>

      <footer>
        <div class="button--wrap">
          <router-link :to="{ name: 'dashboard' }" replace class="cancel" tag="button">
            <i class="icon icon--prev-gray-size16"></i>
            <span>{{ $t('common.list') }}</span>
          </router-link>
          <div v-if="!isEditMode">
            <div class="btn__step">
              <button v-if="hasPrevStep && false" class="btn__pre-step" type="button"
                      :class="{ on: isStepAvailable(prevStep) }"
                      :disabled="!isStepAvailable(prevStep)"
                      @click="onStepClick(prevStep)">
                <i class="icon icon--prev-gray-size16"></i>
                <span>{{ $t('common.prev') }}</span>
              </button>

              <ui-button v-if="hasPrevStep"
                         primary3
                         class="btn__pre-step"
                         :iconName="'icon--prev-gray-size16'"
                         :class="{ on: isStepAvailable(prevStep) }"
                         :disabled="!isStepAvailable(prevStep)"
                         @click="onStepClick(prevStep)">
                {{ $t('common.prev') }}
              </ui-button>

              <button v-if="hasNextStep && false" class="btn__next-step"
                      :class="{ on: isStepAvailable(nextStep) }"
                      :disabled="!isStepAvailable(nextStep)"
                      @click="onStepClick(nextStep)">
                <span>{{ $t('common.next') }}</span>
                <i class="icon icon--next-black-size16" :class="isStepAvailable(nextStep) ? 'icon--next-red-size16' : 'icon--next-black-size16'"></i>
              </button>

              <ui-button v-if="hasNextStep" class="btn__next-step"
                         primary2
                         :iconName="'icon--next-red-size16'"
                         :class="{ on: isStepAvailable(nextStep) }"
                         :disabled="!isStepAvailable(nextStep)"
                         @click="onStepClick(nextStep)">
                {{ $t('common.next') }}
              </ui-button>

              <ui-tooltip v-show="currentStep === 1 && isStep1Valid" class="alert--wrap able__next-step" arrowCenter :transitionType="'slide-bounce'">
                <p v-html="$t('page.footer.step1Pass')"></p>
              </ui-tooltip>
            </div>
          </div>
          <div>
            <button v-if="isEditMode"
                    class="btn__cancel"
                    type="button"
                    @click="$router.replace({ name: 'dashboard' })">{{ $t('common.cancel') }}</button>

            <button class="btn__register" type="submit"
                    :class="{on: canSubmit, disabled: !canSubmit}"
                    :disabled="!canSubmit"
                    @click="onSubmitClick">
              <i class="icon icon--check-white-size20"></i>
              <span>{{ isEditMode ? $t('common.editCompleted') : $t('common.register') }}</span>
            </button>

            <ui-tooltip v-show="!isEditMode && currentStep === 2 && isStep2Valid"
                        class="alert--wrap able__register" :transitionType="'slide-bounce'">
              <span v-html="$t('page.footer.registable')"></span>
            </ui-tooltip>
          </div>
        </div>
      </footer>

      <RenderingLayer
        v-if="showRenderingLayer"
        :captureResult="captureResult"
        :captureState="captureState"
        @closeLayer="cancelPreviewCapture"
        @onCaptureDone="onCaptureDone"/>
      <PreviewLayer v-if="showPreviewLayer">
        <registration-step4
          :layer="'popup'"
          :stepData="stepData"
          :currentStep="currentStep"
          :captureState="captureState"
          :captureResult="captureResult"
          :bMobile="model.bMobile"
          :canCapturePreview="canCapturePreview"
          :selectedDevice.sync="selectedDevice"
          @openShowRenderingLayer="startPreviewCapture"
          @onPreviewLayerClose="showPreviewLayer = false"/>
      </PreviewLayer>
      <cannot-preview v-if="showCannotPreview"
        @onCloseClick="showCannotPreview = false"/>
    </div>
    <tracking-code-guide
      v-else
      :sid="trackingCodeGuideData.sid"
      :realDomain="trackingCodeGuideData.realDomain"/>
    <check-dup-url
      v-if="dupPageList.length"
      @onSubmit="onSubmit"
      @onCancel="onReset"
      :canSubmit="canSubmit"
      :pageList="dupPageList"/>
    <dimmed v-if="showFinishPopup">
      <content-box class="page__registration_end" :width="'430px'">
        <span class="icon icon--check-document"></span>
        <p class="title" v-html="$t('page.requestSubmitted.title')"></p>
        <p class="desc">
          {{ $t('page.requestSubmitted.desc') }}<br>
          {{ $t('page.requestSubmitted.desc2') }}
        </p>
        <ui-button @click="$router.push({ name: 'dashboard', query: { domain: realDomain } })">{{ $t('common.confirm') }}</ui-button>
      </content-box>
    </dimmed>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { format } from 'date-fns'
import { isValidUrl, isValidRegex } from '@/libs/validation-helper'
import { validateUrl, insertApply, checkInstall, getApply, checkDupUrl } from '@/api/apply'
import { requestPreviewCapture, getPreviewCaptureStatus } from '@/api/capture'
import { pick } from '@/libs/utils'
import {
  matchTypeOptions,
  getHostname,
  removeWww,
  stepData,
  MAX_STEP, URL_STATE,
  CAPTURE_STATE,
  CAPTURE_SUCCESS
} from '@/libs/apply-helper'
import RegistrationGuide from '@/components/page-registration/RegistrationGuide'
import RegistrationStep1 from '@/components/page-registration/RegistrationStep1'
import RegistrationStep2 from '@/components/page-registration/RegistrationStep2'
import RegistrationStep3 from '@/components/page-registration/RegistrationStep3'
import RegistrationStep4 from '@/components/page-registration/RegistrationStep4'
import RenderingLayer from '@/components/page-registration/RenderingLayer'
import PreviewLayer from '@/components/page-registration/PreviewLayer'
import CannotPreview from '@/components/page-registration/CannotPreview'
import TrackingCodeGuide from '@/components/page-registration/TrackingCodeGuide'
import CheckDupUrl from '@/components/page-registration/CheckDupUrl'
import UiButton from '@/components/ui/UiButton'
import UiTooltip from '@/components/ui/UiTooltip'
import Dimmed from '@/components/ui/Dimmed'
import ContentBox from '@/components/ui/ContentBox'
import countryCode from '@/data/country-code'
import urlMapping from '@/data/url-mapping'

export default {
  name: 'PageAddOrEdit',
  components: {
    RegistrationGuide,
    RegistrationStep1,
    RegistrationStep2,
    RegistrationStep3,
    RegistrationStep4,
    RenderingLayer,
    PreviewLayer,
    CannotPreview,
    TrackingCodeGuide,
    CheckDupUrl,
    UiButton,
    UiTooltip,
    Dimmed,
    ContentBox
  },
  data () {
    return {
      currentStep: 1,
      showMatchTypeGuide: false,
      useCaptureUrl: false,
      urlState: URL_STATE.unvalidated, // server-side validation state
      captureUrlState: URL_STATE.unvalidated, // same as above
      captureResult: null,
      captureState: CAPTURE_STATE.none,
      stepData: stepData,
      matchTypeOptions: matchTypeOptions,
      selectedDevice: null, // device name of capture preview
      groupNameDirect: '',
      editApplyState: null,
      editApplyRegDate: null,
      allSiteCodeList: [],
      // 서버로 전송할 데이터
      model: {
        siteCode: '',
        groupName: this.$t('common.untitled'),
        pageName: '',
        matchType: 'exact',
        url: '',
        captureUrl: '',
        matchString: '',
        bLogin: false,
        bMobile: false,
        // 이하 옵션
        pvLimit: -1,
        startDate: null,
        endDate: null,
        exceptionSelector: '',
        timezone: null,
        timezoneName: null,
        meta: null
      },
      useOptions: {
        captureUrl: false
      },
      showRenderingLayer: false,
      showPreviewLayer: false,
      showCannotPreview: false,
      trackingCodeGuideData: null,
      dupPageList: [],
      realDomain: null,
      showFinishPopup: false,
      isIng: false
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    title () {
      return this.isEditMode ? this.$t('page.edit.title') : this.$t('page.registration.title')
    },
    hasPrevStep () {
      return this.currentStep > 1
    },
    hasNextStep () {
      return this.currentStep < MAX_STEP
    },
    prevStep () {
      return Math.max(1, this.currentStep - 1)
    },
    nextStep () {
      return Math.min(MAX_STEP, this.currentStep + 1)
    },
    isStep1Valid () {
      return !this.urlError && !this.matchStringError && !this.captureUrlError
    },
    isStep2Valid () {
      if (this.model.groupName === -1) {
        return this.groupNameDirect.length && this.model.pageName.length
      } else {
        return this.model.groupName.length && this.model.pageName.length
      }
    },
    isStep3Valid () {
      const pvLimit = this.model.pvLimit
      return !isNaN(pvLimit) && (pvLimit === -1 || pvLimit > 0)
    },
    isValidAll () {
      return this.isStep1Valid && this.isStep2Valid && this.isStep3Valid
    },
    urlError () {
      const url = this.model.url.trim()

      if (!url.length) {
        if (this.requireDomain) {
          return 'requiredDomain'
        } else {
          return 'requiredUrl'
        }
      } else if (!isValidUrl(url)) {
        if (this.requireDomain) {
          return 'diffFormatDomain'
        } else {
          return 'diffFormatUrl'
        }
      } else {
        if (this.$route.name === 'page-add') {
          const allowedSiteCodeList = this.userData.siteCodeList.includes('ALL')
            ? this.allSiteCodeList
            : this.userData.originalSiteCodeList

          if (!(this.urlSiteCode && allowedSiteCodeList.includes(this.urlSiteCode))) {
            return 'notIncludingSiteCode'
          }
        }
        if (this.urlState === URL_STATE.invalid) {
          if (this.requireDomain) {
            return 'invalidDomain'
          } else {
            return 'invalidUrl'
          }
        } else if (this.urlState === URL_STATE.abExist) {
          return 'alreadyExistAB'
        } else {
          return null
        }
      }
    },
    decomposedUrl () {
      const url = this.model.url.trim()
      let parser = document.createElement('a')
      parser.href = url
      const paths = parser.pathname.split('/')
      if (paths[0] === '') {
        paths.shift()
      }
      if (paths[paths.length - 1] === '') {
        paths.pop()
      }
      return paths
    },
    urlSiteCode () {
      if (this.decomposedUrl[0] && this.allSiteCodeList.includes(this.decomposedUrl[0].toUpperCase())) {
        return this.decomposedUrl[0].toUpperCase()
      }
      return null
    },
    matchStringError () {
      if (!this.requireDomain) {
        return null
      }

      const matchType = this.model.matchType
      const matchString = this.model.matchString
      const isRegex = matchType === 'regexp'

      if (!matchString.length) {
        if (isRegex) {
          return 'requiredRegExp'
        } else {
          return 'requiredMatchString'
        }
      } else if (isRegex && !isValidRegex(matchString)) {
        return 'invalidRegExp'
      } else {
        return null
      }
    },
    captureUrlError () {
      if (!this.useCaptureUrl) {
        return null
      }

      const captureUrl = this.model.captureUrl

      if (!captureUrl.length) {
        return 'requiredUrl'
      } else if (!isValidUrl(captureUrl)) {
        return 'diffFormatUrl'
      } else if (this.captureUrlState === URL_STATE.invalid) {
        return 'invalidUrl'
      }
      return null
    },
    matchTypeData () {
      return this.matchTypeOptions.find(item => item.value === this.model.matchType)
    },
    requireDomain () {
      return this.matchTypeData.requireDomain
    },
    canCapturePreview () {
      if (this.model.bLogin) {
        return false
      }

      if (this.matchTypeData.validationType === 'captureUrl' &&
        (!this.useCaptureUrl || this.captureUrlError)) {
        return false
      }
      return true
    },
    canSubmit () {
      if (this.isEditMode) {
        return this.isValidAll && !this.isIng
      } else {
        return this.isValidAll && !this.isIng && this.currentStep !== 1
      }
    }
  },
  watch: {
    'model.url' (value) {
      this.urlState = URL_STATE.unvalidated
      this.resetCapturePreview()

      if (!this.isEditMode) {
        if (this.$route.name === 'page-add') {
          this.model.siteCode = this.urlSiteCode
        } else {
          this.model.siteCode = 'ETC_' + this.userData.userId.toUpperCase()
        }

        let group = null
        if (this.urlSiteCode && !this.decomposedUrl[1]) {
          group = 'Home'
        } else {
          if (this.decomposedUrl[1] && urlMapping[this.decomposedUrl[1]]) {
            group = urlMapping[this.decomposedUrl[1]]
          }
        }

        this.model.groupName = group || this.$t('common.untitled')
      }
    },
    'model.matchType' (value) {
      this.urlState = URL_STATE.unvalidated
      this.captureUrlState = URL_STATE.unvalidated
      this.resetCapturePreview()
    },
    'model.captureUrl' (value) {
      this.captureUrlState = URL_STATE.unvalidated
      this.resetCapturePreview()
    }
  },
  created () {
    this.isEditMode = this.$route.name === 'page-edit'
    this.CAPTURE_STATE = CAPTURE_STATE

    if (this.isEditMode) {
      this.aid = this.$route.params.aid
      getApply(this.aid).then(data => {
        this.model = {
          siteCode: data.siteCode,
          groupName: data.category,
          pageName: data.pageName,
          matchType: data.matchType,
          url: data.url,
          captureUrl: data.captureURL || '',
          matchString: data.matchString || '',
          bLogin: data.bLogin,
          bMobile: data.bMobile,
          pvLimit: data.pvLimit,
          startDate: data.startDate,
          endDate: data.endDate,
          exceptionSelector: data.exceptionSelector,
          realDomain: data.real_domain,
          timezone: data.timezone,
          timezoneName: data.timezoneName,
          meta: data.meta
        }
        this.useCaptureUrl = this.model.captureUrl.length
        this.editApplyState = data.state
        this.editApplyRegDate = data.regDate
      }).catch(err => {
        console.error(err)
      })
    } else {
      const defaultEndDate = new Date()
      defaultEndDate.setDate(new Date().getDate() + 183)
      this.model.endDate = format(defaultEndDate, 'YYYY-MM-DD') + ' 23:59:59'
      this.model.timezone = this.userData.timezone
      this.model.timezoneName = this.userData.timezoneName
    }

    Object.entries(countryCode).map(([key, value]) => {
      Object.entries(value.country).map(([k, v]) => {
        if (k !== 'ALL') {
          this.allSiteCodeList.push(k)
        }
      })
    })
  },
  methods: {
    onReset () {
      if (this.isEditMode) {
        window.location.reload()
        return
      }

      this.resetCapturePreview()
      this.captureState = CAPTURE_STATE
      this.currentStep = 1
      this.useCaptureUrl = false
      this.urlState = URL_STATE.unvalidated
      this.captureUrlState = this.urlState
      this.stepData = stepData
      this.matchTypeOptions = matchTypeOptions
      this.selectedDevice = null
      this.groupNameDirect = ''
      this.editApplyState = null
      this.editApplyRegDate = null
      this.useOptions.captureUrl = false
      this.trackingCodeGuideData = null
      this.dupPageList = []
      this.realDomain = null

      const defaultEndDate = new Date()
      defaultEndDate.setDate(new Date().getDate() + 183)
      this.model.endDate = format(defaultEndDate, 'YYYY-MM-DD') + ' 23:59:59'
      this.model.siteCode = ''
      this.model.groupName = this.$t('common.untitled')
      this.model.pageName = ''
      this.model.matchType = 'exact'
      this.model.url = ''
      this.model.captureUrl = ''
      this.model.matchString = ''
      this.model.bLogin = false
      this.model.bMobile = false
      this.model.pvLimit = -1
      this.model.startDate = false
      this.model.exceptionSelector = ''
      this.model.timezone = this.userData.timezone
      this.model.timezoneName = this.userData.timezoneName
      this.model.meta = null

      this.showMatchTypeGuide = false
      this.showRenderingLayer = false
      this.showPreviewLayer = false
      this.showCannotPreview = false
      this.showFinishPopup = false
    },
    hideLayer () {
      this.showMatchTypeGuide = false
    },
    resetCapturePreview () {
      this.captureResult = null
      this.captureState = CAPTURE_STATE.none
    },
    isStepAvailable (step) {
      switch (step) {
        case 1:
          return true
        case 2:
          return this.isStep1Valid
        default:
          return this.isStep1Valid && this.isStep2Valid
      }
    },
    validateUrl () {
      const model = this.model
      return validateUrl(model.url, model.bMobile, false).then(data => {
        this.urlState = URL_STATE.valid
        return pick(data, [
          'url',
          'domain',
          'realDomain',
          'hostname',
          'pathname',
          'search',
          'hash',
          'origin'
        ])
      }).catch(err => {
        if (err.message === 'URL_IN_ABLIST') {
          this.urlState = URL_STATE.abExist
        } else {
          this.urlState = URL_STATE.invalid
        }
        err.isHandled = true
        throw err
      })
    },
    validateCaptureUrl () {
      const model = this.model
      return validateUrl(model.captureUrl, model.bMobile, true).then(() => {
        this.captureUrlState = URL_STATE.valid
        const url = model.url
        const hostname = getHostname(url)
        const domain = removeWww(hostname)
        const realDomain = removeWww(hostname)
        return {
          url,
          hostname,
          domain,
          realDomain
        }
      }).catch(err => {
        this.captureUrlState = URL_STATE.invalid
        err.isHandled = true
        throw err
      })
    },
    validateStaticUrl () {
      const url = this.model.url
      const hostname = getHostname(url)
      const domain = removeWww(hostname)
      const realDomain = removeWww(hostname)
      return Promise.resolve({
        url,
        hostname,
        domain,
        realDomain
      })
    },
    validateUrlState () {
      const validationType = this.matchTypeData.validationType

      if (validationType === 'url') {
        if (this.model.captureUrl) {
          return new Promise((resolve, reject) => {
            this.validateUrl().then(data => {
              this.validateCaptureUrl().then(() => {
                resolve(data)
              }).catch(err => {
                reject(err)
              })
            }).catch(err => {
              reject(err)
            })
          })
        } else {
          return this.validateUrl()
        }
      } else if (validationType === 'captureUrl' && this.model.captureUrl.length) {
        return this.validateCaptureUrl()
      } else {
        return this.validateStaticUrl()
      }
    },
    onStepClick (step) {
      if (step === this.currentStep) {
        return
      }

      if (this.isEditMode && step === 4 && this.editApplyState === 'run') {
        this.showCannotPreview = true
        return
      }

      if (this.isStepAvailable(step)) {
        let requireUrlValidation = false

        if (this.currentStep === 1 && step > 1) {
          requireUrlValidation = this.urlState !== URL_STATE.valid

          if (this.useCaptureUrl) {
            requireUrlValidation = requireUrlValidation || this.captureUrlState !== URL_STATE.valid
          }
        }

        if (!this.isEditMode && requireUrlValidation) {
          this.validateUrlState().then(result => {
            if (result) {
              this.currentStep = step
            }
          }).catch(err => {
            console.error(err)
          })
        } else {
          this.currentStep = step
        }
      }
    },
    validateUrlFirst () {
      if (!this.urlError) {
        let requireUrlValidation = this.urlState !== URL_STATE.valid
        if (this.useCaptureUrl) {
          requireUrlValidation = requireUrlValidation || this.captureUrlState !== URL_STATE.valid
        }

        if (requireUrlValidation) {
          this.validateUrlState().catch(err => {
            console.error(err)
          })
        }
      }
    },
    getPostData (validationData) {
      validationData = validationData || {}
      const model = this.model
      const data = {
        siteCode: model.siteCode,
        category: model.groupName === -1 ? this.groupNameDirect : model.groupName,
        pageName: model.pageName || null,
        matchType: model.matchType,
        matchString: model.matchString || null,
        pvLimit: model.pvLimit,
        bLogin: model.bLogin,
        bMobile: model.bMobile,
        captureUrl: model.captureUrl || null,
        startDate: model.startDate ? format(model.startDate, 'YYYY-MM-DD') : null,
        endDate: model.endDate ? format(model.endDate, 'YYYY-MM-DD') : null,
        timezone: model.timezone,
        timezoneName: model.timezoneName,
        inputUrl: model.url || null,
        exceptionSelector: model.exceptionSelector || null,
        url: validationData.url || null,
        domain: validationData.domain || null,
        realDomain: validationData.realDomain || null,
        hostname: validationData.hostname || null,
        origin: validationData.origin || null,
        pathname: validationData.pathname || null,
        search: validationData.search || null,
        hash: validationData.hash || null
      }

      if (data.url.charAt(data.url.length - 1) === '#') {
        data.url = data.url.substr(0, data.url.length - 1)
      }
      return data
    },
    onEditClick () {
      if (this.isIng) {
        return
      }
      this.isIng = true
      this.isIng = true
      const model = this.model

      ;(() => {
        if (model.captureUrl.length) {
          return this.validateCaptureUrl()
        } else {
          return Promise.resolve()
        }
      })().then(() => {
        const postData = {
          id: this.aid,
          category: model.groupName === -1 ? this.groupNameDirect : model.groupName,
          pageName: model.pageName || null,
          bLogin: model.bLogin,
          bMobile: model.bMobile,
          captureUrl: model.captureUrl || null,
          pvLimit: model.pvLimit || -1,
          startDate: model.startDate ? format(model.startDate, 'YYYY-MM-DD') : null,
          endDate: model.endDate ? format(model.endDate, 'YYYY-MM-DD') : null,
          exceptionSelector: model.exceptionSelector || null
        }
        return insertApply(postData)
      }).then(() => {
        this.$router.push({ name: 'dashboard', query: { domain: model.realDomain } })
      }).catch(err => {
        if (!err.isHandled) {
          console.error(err)
        }
        this.isIng = false
      })
    },
    onSubmitClick () {
      if (this.isEditMode) {
        return this.onEditClick()
      }

      if (this.isIng) {
        return
      }

      this.isIng = true
      this.dupPageList = []
      if (['exact', 'simple'].includes(this.model.matchType)) {
        this.validateUrlState().then(validateData => {
          if (validateData) {
            checkDupUrl({
              matchType: this.model.matchType,
              url: validateData.url
            }).then(result => {
              if (result.length) {
                this.dupPageList = result
              } else {
                this.onSubmit()
              }
              this.isIng = false
            })
          }
        })
      } else {
        this.onSubmit()
        this.isIng = false
      }
    },
    onSubmit () {
      this.dupPageList = []
      this.isIng = true
      this.validateUrlState().then(validateData => {
        if (validateData) {
          return this.getPostData(validateData)
        }
      }).then(postData => {
        if (postData) {
          return insertApply(postData)
        }
      }).then(applyData => {
        if (applyData && applyData.url) {
          console.info('apply added')
          console.info(applyData)

          return new Promise((resolve, reject) => {
            const result = {
              applyData,
              showCodeLayer: false
            }

            if (applyData.isNewSid) {
              result.showCodeLayer = true
              resolve(result)
            } else {
              const canInstallCheck = !applyData.bLogin && ['exact', 'simple'].includes(applyData.matchType)

              if (canInstallCheck) {
                checkInstall(applyData._id).then(() => {
                  resolve(result)
                }).catch(err => {
                  switch (err.message) {
                    case 'Not installed':
                    case 'Pending page':
                      result.showCodeLayer = true
                  }
                  // no alert if install check failed
                  resolve(result)
                })
              } else {
                resolve(result)
              }
            }
          })
        } else {
          throw new Error('NO_APPLY')
        }
      }).then(({ applyData, showCodeLayer }) => {
        this.isIng = false
        const realDomain = applyData.realDomain || applyData.real_domain

        if (showCodeLayer) {
          this.trackingCodeGuideData = {
            realDomain,
            sid: applyData.sid
          }
        } else {
          if (this.userData.isGmc) {
            this.$router.push({ name: 'dashboard', query: { domain: realDomain } })
          } else {
            this.realDomain = realDomain
            this.showFinishPopup = true
          }
        }
      }).catch(err => {
        if (err.message === 'URL_IN_ABLIST') {
          this.urlState = URL_STATE.abExist
          this.onStepClick(1)
        } else if (!err.isHandled) {
          alert('apply failed')
          console.error(err)
        }
        this.isIng = false
      })
    },
    startPreviewCapture (isRetake) {
      if (!this.canCapturePreview) {
        return
      }
      this.showPreviewLayer = false

      if (this.isRetake) {
        this.captureResult = null
      }
      this.showRenderingLayer = true
      this.captureState = CAPTURE_STATE.waiting
      const url = this.model.captureUrl || this.model.url

      requestPreviewCapture({
        url,
        isMobile: this.model.bMobile,
        exceptionSelector: this.model.exceptionSelector
      }).then(data => {
        return this.getCapturePreview(data.requestId, data.uuid)
      }).then(result => {
        window.clearInterval(this.previewTimer)
        window.setTimeout(() => {
          const tasks = this.captureResult.tasks
          if (tasks) {
            const successTasks = tasks.filter(item => {
              return item.statusCode === CAPTURE_SUCCESS
            })

            if (successTasks.length === tasks.length) {
              this.selectedDevice = tasks[0].deviceName
              this.captureState = CAPTURE_STATE.done
              this.showRenderingLayer = false
            } else {
              this.captureState = CAPTURE_STATE.failed
            }
          }
        }, 500)
      }).catch(err => {
        this.cancelPreviewCapture()
        console.error(err)
      })
    },
    getCapturePreview (id, uuid) {
      return new Promise((resolve, reject) => {
        this.captureResult = null
        window.clearInterval(this.previewTimer)

        const intervalFn = () => {
          getPreviewCaptureStatus(id, uuid).then(data => {
            this.captureResult = data

            if (this.captureResult.waitCount === 0) {
              this.captureState = CAPTURE_STATE.processing
            }
            const taskSize = data.tasks.length
            const doneTasks = data.tasks.filter(item => {
              return item.statusCode === CAPTURE_SUCCESS || item.statusCode < 0
            })
            if (taskSize === doneTasks.length) {
              resolve()
            }
          }).catch(err => {
            this.cancelPreviewCapture()
            reject(err)
          })
        }

        this.previewTimer = window.setInterval(intervalFn, 5000)
        intervalFn()
      })
    },
    cancelPreviewCapture () {
      window.clearInterval(this.previewTimer)
      this.resetCapturePreview()
      this.showRenderingLayer = false
    },
    onCaptureDone () {
      this.captureState = CAPTURE_STATE.done
      const tasks = this.captureResult.tasks
      const firstSuccessTask = find(item => item.statusCode === CAPTURE_SUCCESS)

      if (firstSuccessTask) {
        this.selectedDevice = firstSuccessTask.deviceName
      } else {
        this.selectedDevice = tasks[0].deviceName
      }
      this.showRenderingLayer = false
    },
    onTimezoneChange (value) {
      this.model.startDate = null
      const defaultEndDate = new Date()
      defaultEndDate.setDate(new Date().getDate() + 183)
      this.model.endDate = format(defaultEndDate, 'YYYY-MM-DD') + ' 23:59:59'
      this.model.timezone = value.offset
      this.model.timezoneName = value.timezone
    }
  },
  destroyed () {
    this.cancelPreviewCapture()
  }
}
</script>

<style lang="scss">
.page__registration {
  width: 1250px;
  margin: 0 auto;

  & > div {
    padding-bottom: 80px;
  }

  .page__registration__step--wrap {
    padding-top: 56px;
    position: relative;
    & > .title {
      position: absolute;
      bottom: 0;
      left: 15px;
      font-size: 26px;
      color: #444444;
      font-weight: bold;
      line-height: 1;
      text-align: center;
    }
    .step--wrap {
      display: flex;
      justify-content: center;
    }
    .step__list {
      &.essential {
        width: 243px;
        position: relative;

        &:before {
          content: '';
          display: block;
          width: 100%;
          height: 1px;
          border-top: 2px solid #d7d7d7;
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
        }

        .list-item {
          &.available {
            border: none;
            cursor: pointer;
          }

          &.step2__available {
            .icon--wrap {
              border: 1px solid #999;
            }
            .step-num {
              color: #888;
            }
            .step-level,
            .step-title {
              color: #666666;
            }
          }

          &.on{
            .icon--wrap {
              border-color: $brandColor;
              background-color: $brandColor;
            }
            .step-num {
              color: #ffffff;
            }
            .step-level,
            .step-title {
              color: $brandColor;
            }
          }

          .icon--wrap.pass1,
          .icon--wrap.pass2 {
            background-color: #57ab00;
            border: none;
            .icon {
              display: inline-block;
              border: none;
              width: 16px;
              height: 16px;
              background-size: cover;
            }
            .step-num {
              display: none;
            }
            .step-title,
            .step-level {
              color: #57ab00;
            }
          }
        }
      }

      &.optional {
        position: relative;

        &:before {
          content: '';
          display: block;
          width: 100%;
          height: 1px;
          border-top: 2px dashed #d7d7d7;
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
        }

        .list-item {
          margin-left: 170px;
          background-color: #f4f4f1;

          &:nth-of-type(1) {
            margin-left: 155px;
          }

          &.available{
            .icon--wrap {
              border-color: #999999;

              .icon--circle {
                background-color: #888888;
              }

              .step-title,
              .step-level {
                color: #666;
              }
            }
          }

          &.on {
            .icon--wrap {
              border-color: $brandColor;

              .step-title,
              .step-level {
                color: $brandColor;
              }

              .icon--circle {
                background-color: $brandColor;
              }
            }
          }

          .icon--circle {
            display: inline-block;
            width: 8px;
            height: 8px;
            background-color: #d7d7d7;
            border-radius: 50%;
            @include positionXYcenter;
          }
        }
      }

      .list-item {
        display: inline-block;
        position: relative;
        width: 28px;
        height: 28px;

        &.available,
        &.free {
          cursor: pointer;
        }

        &:nth-last-of-type(1) {
          float: right;
        }

        .icon--wrap {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          display: inline-block;
          width: 100%;
          height: 100%;
          background-color: #ffffff;
          border: solid 1px #d7d7d7;
          border-radius: 50%;
          box-sizing: border-box;
          text-align: center;

          .step-num {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 14px;
            font-weight: bold;
            line-height: 1;
            color: rgba(102, 102, 102, 0.5);
          }

          .icon {
            @include positionXYcenter;
            display: none;
          }
        }

        .step-title {
          position: absolute;
          left: 50%;
          top: calc(100% + 8px);
          transform: translateX(-50%);
          text-align: center;
          font-size: 14px;
          white-space: nowrap;
          color: rgba(102, 102, 102, 0.5);
        }

        .step-level {
          display: inline-block;
          font-size: 12px;
          font-weight: bold;
          white-space: nowrap;
          position: absolute;
          left: 50%;
          bottom: calc(100% + 8px);
          transform: translateX(-50%);
          color: rgba(102, 102, 102, 0.5);
        }
      }
    }
    .edit-step--wrap {
      margin-left: 14px;
    }
    .edit-step__list {
      .list-item {
        cursor: pointer;
        float: left;
        margin-right: 30px;
        padding: 0 5px 7px 5px;
        border-bottom: 3px solid transparent;

        .step-title {
          font-size: 15px;
          color: #999;
        }

        &.on {
          border-bottom-color: #ec0047;
          .step-title {
            font-weight: bold;
            color: #2f2f2f;
          }
        }
      }
    }
  }

  .page__registration__content--wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 60px;

    &.page_edit {
      margin-top: 24px;
    }

    .page__registration__content {
      display: inline-block;
      height: 500px;
      border-radius: 34px;

      &.left {
        width: 740px;
        overflow: hidden;
        background-color: #ffffff;
        box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);

        &.step4 {
          overflow: inherit;
          position: relative;
        }

        .step__box {
          input[type=radio] {
            opacity: 0;
            width: 0;
            height: 0;
            position: absolute;
            top: 0;
            left: 0;
            overflow: hidden;
            text-indent: -9999px;
          }

          .validation-text {
            font-size: 12px;
            font-weight: bold;
            color: $errorColor;
            padding-left: 6px;
          }

          .input--wrap {
            position: relative;
            display: inline-block;

            input[type=text] {
              display: block;
              height: 38px;
              line-height: 36px;
              border-radius: 6px;
              border: solid 1px #bbbbbb;
              background-color: #ffffff;
              font-size: 14px;
              padding-left: 12px;
              padding-right: 40px;
              box-sizing: border-box;

              &:focus {
                border-color: #289eef;
              }
              &[disabled] {
                background-color: #d7d7d7;
                opacity: 0.3;
              }
            }

            .icon--check-green-size16 {
              display: none;
            }

            .icon--close14, .icon--check-green-size16{
              position: absolute;
              top: 37px;
              right: 2px;
              transform: translateY(-50%);
              padding: 10px 12px;
              background-position: center;
              cursor: pointer;
            }

            .validation-text {
              position: absolute;
              bottom: -20px;
              left: 0;
            }
          }

          .step-title {
            text-align: center;
            margin-top: 35px;
            .title__text {
              position: relative;
              display: inline-block;
              font-size: 18px;
              font-weight: bold;
              line-height: 1;
              color: #2f2f2f;
            }
            .icon {
              position: absolute;
              top: 50%;
              right: 0;
              transform: translateY(-50%);
            }
          }

          .step-content {
            .label {
              font-size: 13px;
              font-weight: bold;
              color: #767676;
              margin-bottom: 5px;
              padding-left: 6px;
            }

            .button--wrap {
              margin-top: 35px;
              text-align: center;
              .btn-next {
                @include buttonTypeNormal;
                border: 1px solid #bbbbbb;
                font-size: 14px;

                &.disabled {
                  opacity: 0.3;
                  pointer-events: none;
                }
              }
            }
          }
        }
      }
      &.right {
        width: 480px;
        background-color: #2f2f2f;
      }
    }

    .divide {
      width: 1px;
      height: 440px;
      border-left: 1px solid #d7d7d7;
      margin-left: 15px;
      margin-right: 15px;
    }
  }

  footer {
    border-top: 2px solid #d7d7d7;
    margin-top: 30px;
    width: 100%;
    min-width: 1024px;
    height: 68px;
    position: relative;

    .button--wrap {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0 auto;
      width: 100%;
      min-width: 1024px;
      max-width: 1280px;
      height: 100%;

      .cancel {
        position: relative;
        height: 34px;
        font-size: 13px;
        font-weight: bold;
        color: #444444;
        padding-left: 35px;
        padding-right: 11px;
        border-radius: 19px;

        &:hover {
          background-color: rgba(255, 255, 255, 0.16);
        }

        .icon {
          position: absolute;
          top: 50%;
          left: 11px;
          transform: translateY(-50%);
        }

        span {
          line-height: 34px;
        }
      }
      .btn__step {
        @include positionXYcenter;
        z-index: 1;

        .btn__pre-step,
        .btn__next-step {
          border-radius: 22px;
          min-width: 110px;
          height: 44px;
          font-size: 16px;
          font-weight: bold;
        }

        .btn__pre-step {
          position: relative;
          margin-right: 5px;
          text-align: right;
          padding-right: 26px;

          .icon {
            position: absolute;
            top: 50%;
            left: 14px;
            transform: translateY(-50%);
          }

          .text {
            margin-right: 0;
          }
        }

        .btn__next-step {
          position: relative;
          text-align: left;
          padding-left: 26px;

          &:hover {
            .icon {
              background-size: contain;
              background-image: url('../../../assets/img/icon_prev_white_size20.svg');
              transform: rotateY(180deg) translateY(-50%);
            }
          }

          .icon {
            position: absolute;
            top: 50%;
            left: auto;
            right: 14px;
            transform: translateY(-50%);
          }
        }
      }

      .btn__register,
      .btn__cancel {
        cursor: default;
        position: relative;
        height: 44px;
        border-radius: 22px;
        padding-left: 60px;
        padding-right: 18px;
        font-size: 16px;
        font-weight: bold;
        &.on {
          cursor: pointer;
          .icon, span {
            opacity: 1;
          }
        }
        .icon {
          position: absolute;
          top: 50%;
          left: 14px;
          transform: translateY(-50%);
        }
        .icon, span {
          opacity: 0.3;
        }
      }

      .btn__register {
        margin-left: 5px;
        border: 0;
        background-color: $brandColor;
        color: #fff;
        overflow: visible;

        &:hover {
          &:after {
            background-color: rgba(0, 0, 0, 0.16);
          }
        }

        &:active {
          &:after {
            background-color: rgba(0, 0, 0, 0.32);
          }
        }

        &.disabled {
          background-color: rgba(0, 0, 0, 0.16) !important;
        }
      }

      .btn__cancel {
        cursor: pointer;
        padding: 0 34px;
        pointer-events: auto;

        border: 1px solid #bbb;
        color: #444;
        background-color: #fff;
        overflow: hidden;

        &:hover {
          &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.06);
          }
        }

        &:active {
          &:after {
            background-color: rgba(0, 0, 0, 0.16);
          }
        }

        &.disabled {
          border-color: rgba(187, 187, 187, 0.2)
        }
      }
    }

    .alert--wrap {
      border-radius: 8px;
      box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
      background-color: #ffffff;
      padding: 15px 20px;
      box-sizing: border-box;
      text-align: left;

      &.able__next-step {
        position: absolute;
        left: 50%;
        bottom: calc(100% + 19px);
        transform: translateX(-50%);
        width: 250px;

        &.bubble {
          .text {
            white-space: normal;
            text-align: left;
          }
        }
      }
      &.able__register {
        position: absolute;
        bottom: calc(100% + 7px);
        right: 0;
        width: 290px;
        z-index: 1;

        .text {
          text-align: left;
          white-space: normal;
        }

        .arrow {
          display: none;
        }
      }

      p {
        font-size: 13px;
        line-height: 1.46;
        color: #444444;
      }
    }
  }

  .step4 {
    height: 100%;

    button.icon {
      position: absolute;
      top: 20px;
      right: 20px;
      opacity: 0.3;
      pointer-events: none;

      &.on {
        opacity: 1;
        pointer-events: all;
      }
    }
  }
}

.page__registration.edit-mode {
  .page__registration__step--wrap {
    padding-top: 37px;

    .title {
      position: static;
      text-align: left;
      margin-left: 17px;
    }

    .edit-step--wrap {
      margin-top: 30px;
    }
  }
}

.page__registration_end {
  padding-top: 50px;
  padding-bottom: 25px;
  text-align: center;

  .title {
    font-size: 20px;
    font-weight: bold;
    color: #444444;
    margin-top: 15px;
  }
  .desc {
    font-size: 14px;
    line-height: 1.43;
    color: #2f2f2f;
    margin-top: 12px;
    height: 50px;
    padding: 0 30px;
  }
  .ui-btn {
    margin: 30px 5px 0;
  }
}

@media screen and (min-height: 900px) {
  .page__registration {
    display: flex;
    align-items: center;
    height: calc(100vh - 40px);
  }
}
</style>
